import { useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import type { ExportSettingsType } from '@/features/DataHub/modules/Export/types/context.types';
import { getFromToValues } from '@/features/DataHub/modules/Export/utils';

import {
  buildExportApiRequestString,
  buildExportCurlRequestString
} from '../services/ExportRequestService';
import { useLatestApiKey } from './useLatestApiKey';

type UseExportApiStringsParams = {
  symbols: SymbolKey[];
  exportSettings: ExportSettingsType;
  userTimezone: string;
  gxURL: string;
};

const useExportApiStrings = ({
  symbols,
  exportSettings,
  userTimezone,
  gxURL
}: UseExportApiStringsParams) => {
  const apiKey = useLatestApiKey();
  const { fromValue, toValue } = getFromToValues(exportSettings);

  const baseParams = useMemo(
    () => ({
      gxURL,
      symbols,
      fromValue,
      toValue: toValue || null,
      includeMetadata: exportSettings.includeMetadata,
      userTimezone
    }),
    [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, gxURL]
  );

  const exportApiRequestString = useMemo(
    () => buildExportApiRequestString({ ...baseParams, apiKey }),
    [baseParams, apiKey]
  );

  const exportCurlRequestString = useMemo(
    () => buildExportCurlRequestString({ ...baseParams, apiKey: apiKey ?? '' }),
    [baseParams, apiKey]
  );

  return { exportApiRequestString, exportCurlRequestString };
};

export default useExportApiStrings;
